import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FiCheck } from "react-icons/fi";
import axios from "axios";
import API_BASE_URL from "../../config/api";
import { useAuth } from "../../context/AuthContext";

interface FormData {
  feelingDescription: string;
  subCategory: string;
  subCategoryId: number | null;
  category: string;
  categoryId: number | null;
  addNote: string;
  pleasantness: number;
  impact: number;
  emotion: string;
  calculatedEmotion: string;
  calculatedZone: string;
  date: string;
  time: string;
}

interface Mood {
  id?: number;
  name?: string;
  emotion?: {
    name: string;
  };
  zone?: {
    name: string;
    description?: string;
  };
  status?: boolean;
}

interface SubCategory {
  id: number;
  name: string;
  status: boolean;
  category?: {
    id: number;
    name: string;
  };
  categoryId?: number;
}

interface Category {
  id: number;
  name: string;
  status: boolean;
}

interface Emotion {
  id: number;
  name: string;
  status: boolean;
}

interface Zone {
  id: number;
  name: string;
  status: boolean;
}

interface AutocompleteSuggestion {
  mood: string;
  description: string;
  emotion: string;
  zone: string;
}

interface EmotionZoneResult {
  calculatedEmotion: string;
  calculatedZone: string;
}

const LogMood: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<FormData>({
    feelingDescription: "",
    subCategory: "",
    subCategoryId: null,
    category: "",
    categoryId: null,
    addNote: "",
    // Default to mid‑point on a 1–7 scale
    pleasantness: 4,
    impact: 4,
    emotion: "",
    calculatedEmotion: "",
    calculatedZone: "",
    date: new Date().toISOString().split("T")[0],
    time: new Date().toTimeString().slice(0, 5),
  });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [autocompleteSuggestions, setAutocompleteSuggestions] = useState<
    AutocompleteSuggestion[]
  >([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [moods, setMoods] = useState<Mood[]>([]);
  const [loadingMoods, setLoadingMoods] = useState(false);
  const [subCategories, setSubCategories] = useState<SubCategory[]>([]);
  const [loadingSubCategories, setLoadingSubCategories] = useState(false);
  const [subCategorySuggestions, setSubCategorySuggestions] = useState<
    SubCategory[]
  >([]);
  const [showSubCategorySuggestions, setShowSubCategorySuggestions] =
    useState(false);
  const [selectedSubCategoryIndex, setSelectedSubCategoryIndex] = useState(-1);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedSubCategoryData, setSelectedSubCategoryData] =
    useState<SubCategory | null>(null);
  const [showCategorySelection, setShowCategorySelection] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
  const [emotions, setEmotions] = useState<Emotion[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  const [isOtherSelected, setIsOtherSelected] = useState(false);
  const [otherText, setOtherText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const subCategoryInputRef = useRef<HTMLInputElement>(null);
  const subCategorySuggestionsRef = useRef<HTMLDivElement>(null);

  const SERVER_URL = `${API_BASE_URL}/moods`;
  const SUB_CATEGORY_URL = `${API_BASE_URL}/subcategories`;
  const CATEGORY_URL = `${API_BASE_URL}/categories`;
  const EMOTION_URL = `${API_BASE_URL}/emotions`;
  const ZONE_URL = `${API_BASE_URL}/zones`;

  // Fetch moods from API
  useEffect(() => {
    const fetchMoods = async () => {
      try {
        setLoadingMoods(true);
        const response = await axios.get(`${SERVER_URL}`, {
          params: {
            status: "true",
          },
        });
        if (response.data) {
          setMoods(response.data);
        }
      } catch (err) {
        console.error("Error fetching moods:", err);
        setMoods([]);
      } finally {
        setLoadingMoods(false);
      }
    };

    fetchMoods();
  }, []);

  // Fetch subcategories from API
  useEffect(() => {
    const fetchSubCategories = async () => {
      try {
        setLoadingSubCategories(true);
        const response = await axios.get(`${SUB_CATEGORY_URL}`);
        if (response.data) {
          const activeSubCategories = response.data.filter(
            (subCat: SubCategory) => subCat.status === true
          );
          setSubCategories(activeSubCategories);
        }
      } catch (err) {
        console.error("Error fetching subcategories:", err);
        setSubCategories([]);
      } finally {
        setLoadingSubCategories(false);
      }
    };

    fetchSubCategories();
  }, []);

  // Fetch categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${CATEGORY_URL}`);
        if (response.data) {
          const activeCategories = response.data.filter(
            (cat: Category) => cat.status === true
          );
          setCategories(activeCategories);
        }
      } catch (err) {
        console.error("Error fetching categories:", err);
        setCategories([]);
      }
    };

    fetchCategories();
  }, []);

  // Fetch emotions and zones for calculation
  useEffect(() => {
    const fetchEmotionsAndZones = async () => {
      try {
        const [emotionsRes, zonesRes] = await Promise.all([
          axios.get(`${EMOTION_URL}`),
          axios.get(`${ZONE_URL}`),
        ]);
        if (emotionsRes.data) {
          setEmotions(
            emotionsRes.data.filter((e: Emotion) => e.status === true)
          );
        }
        if (zonesRes.data) {
          setZones(zonesRes.data.filter((z: Zone) => z.status === true && z.name !== "Light Red"));
        }
      } catch (err) {
        console.error("Error fetching emotions/zones:", err);
      }
    };

    fetchEmotionsAndZones();
  }, []);

  // Get pleasantness label
  const getPleasantnessLabel = (value: number): string => {
    if (value <= 2) return "Very Low";
    if (value <= 3) return "Low";
    if (value <= 5) return "Moderate";
    if (value <= 6) return "High";
    return "Very High";
  };

  // Get impact label
  const getImpactLabel = (value: number): string => {
    if (value <= 2) return "Very Low";
    if (value <= 3) return "Low";
    if (value <= 5) return "Moderate";
    if (value <= 6) return "High";
    return "Very High";
  };

  // Handle feeling description input change
  const handleFeelingDescriptionChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const value = e.target.value;
    setFormData({ ...formData, feelingDescription: value });

    if (value.trim().length > 0 && moods.length > 0) {
      const filtered = moods
        .filter(
          (mood) =>
            mood.name?.toLowerCase().includes(value.toLowerCase()) ||
            mood.emotion?.name?.toLowerCase().includes(value.toLowerCase()) ||
            mood.zone?.description
              ?.toLowerCase()
              .includes(value.toLowerCase()) ||
            mood.zone?.name?.toLowerCase().includes(value.toLowerCase())
        )
        .map((mood) => ({
          mood: mood.name || "",
          description: mood.zone?.description || mood.zone?.name || "",
          emotion: mood.emotion?.name || "",
          zone: mood.zone?.name || "",
        }));

      setAutocompleteSuggestions(filtered.slice(0, 5));
      setShowSuggestions(filtered.length > 0);
      setSelectedSuggestionIndex(-1);
    } else {
      setShowSuggestions(false);
      setAutocompleteSuggestions([]);
      setSelectedSuggestionIndex(-1);
    }
  };

  // Handle suggestion selection
  const handleSuggestionSelect = (mood: AutocompleteSuggestion) => {
    setFormData({
      ...formData,
      feelingDescription: mood.mood,
    });
    setShowSuggestions(false);
    setAutocompleteSuggestions([]);
    setSelectedSuggestionIndex(-1);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!showSuggestions || autocompleteSuggestions.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedSuggestionIndex((prev) =>
        prev < autocompleteSuggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedSuggestionIndex((prev) => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === "Enter" && selectedSuggestionIndex >= 0) {
      e.preventDefault();
      handleSuggestionSelect(autocompleteSuggestions[selectedSuggestionIndex]);
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
      setSelectedSuggestionIndex(-1);
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node) &&
        textareaRef.current &&
        !textareaRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
        setSelectedSuggestionIndex(-1);
      }
      if (
        subCategorySuggestionsRef.current &&
        !subCategorySuggestionsRef.current.contains(event.target as Node) &&
        subCategoryInputRef.current &&
        !subCategoryInputRef.current.contains(event.target as Node)
      ) {
        setShowSubCategorySuggestions(false);
        setSelectedSubCategoryIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle "What is on top of your mind?" input change (uses subcategory data)
  const handleSubCategoryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Reset subCategoryId and categoryId when user types
    setFormData({
      ...formData,
      subCategory: value,
      subCategoryId: null,
      category: "",
      categoryId: null,
    });
    setSelectedSubCategoryData(null);
    setIsOtherSelected(false);
    setOtherText("");

    if (value.trim().length > 0 && subCategories.length > 0) {
      const filtered = subCategories.filter((subCat) => {
        const matchesName = subCat.name
          ?.toLowerCase()
          .includes(value.toLowerCase());
        return matchesName;
      });

      setSubCategorySuggestions(filtered.slice(0, 5));
      setShowSubCategorySuggestions(true);
      setSelectedSubCategoryIndex(-1);
    } else if (value.trim().length > 0) {
      // Show dropdown with only "Others" option when no matches
      setSubCategorySuggestions([]);
      setShowSubCategorySuggestions(true);
      setSelectedSubCategoryIndex(-1);
    } else {
      setShowSubCategorySuggestions(false);
      setSubCategorySuggestions([]);
      setSelectedSubCategoryIndex(-1);
    }
  };

  const handleCategorySelect = (category: Category) => {
    // When Life Circle (category) is selected, update form data
    // If we have a selected subcategory, make sure we use the correct subcategory for this category
    if (selectedSubCategoryData) {
      const matchingSubCat = subCategories.find(
        (sc) =>
          sc.name.toLowerCase() === selectedSubCategoryData.name.toLowerCase() &&
          (sc.category?.id === category.id || sc.categoryId === category.id)
      );
      if (matchingSubCat) {
        setFormData((prev) => ({
          ...prev,
          category: category.name,
          categoryId: category.id,
          subCategoryId: matchingSubCat.id,
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          category: category.name,
          categoryId: category.id,
        }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        category: category.name,
        categoryId: category.id,
      }));
    }
    setShowCategorySelection(false);
  };

  // Handle subcategory suggestion selection
  const handleSubCategorySelect = (subCategory: SubCategory) => {
    setSelectedSubCategoryData(subCategory);
    setShowSubCategorySuggestions(false);
    setSubCategorySuggestions([]);
    setSelectedSubCategoryIndex(-1);

    let categoryName = "";
    let categoryId: number | null = null;

    if (subCategory.category) {
      categoryName = subCategory.category.name;
      categoryId = subCategory.category.id;
    } else if (subCategory.categoryId) {
      const category = categories.find((c) => c.id === subCategory.categoryId);
      if (category) {
        categoryName = category.name;
        categoryId = category.id;
      }
    }

    // Check if there are multiple subcategories with the same name in different categories
    const sameNameSubCats = subCategories.filter(
      (sc) => sc.name.toLowerCase() === subCategory.name.toLowerCase()
    );
    
    if (sameNameSubCats.length > 1) {
      // Multiple categories for same subcategory name - show selection
      const uniqueCategoryIds = Array.from(
        new Set(
          sameNameSubCats.map((sc) => sc.category?.id ?? sc.categoryId).filter((id): id is number => id !== undefined && id !== null)
        )
      );
      setSelectedCategories(uniqueCategoryIds);
      // Store the subcategory name but wait for category selection before setting subCategoryId
      setFormData((prev) => ({
        ...prev,
        subCategory: subCategory.name,
        subCategoryId: null, // Don't set yet - wait for category selection
        category: "",
        categoryId: null,
        addNote: "",
      }));
      setShowCategorySelection(true);
    } else {
      // Single category - auto-select
      setShowCategorySelection(false);
      setFormData((prev) => ({
        ...prev,
        subCategory: subCategory.name,
        subCategoryId: subCategory.id,
        category: categoryName,
        categoryId: categoryId,
        addNote: "",
      }));
    }
  };

  // Handle "Others" option selection
  const handleOtherSelect = () => {
    setIsOtherSelected(true);
    setOtherText("");
    setFormData((prev) => ({
      ...prev,
      subCategory: "",
      subCategoryId: null,
      category: "Others",
      categoryId: -1,
    }));
    setShowSubCategorySuggestions(false);
    setSubCategorySuggestions([]);
    setSelectedSubCategoryIndex(-1);
    setShowCategorySelection(true);
    setSelectedCategories(categories.map((c) => c.id));
  };

  // Handle keyboard navigation for subcategory
  const handleSubCategoryKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (!showSubCategorySuggestions || subCategorySuggestions.length === 0)
      return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedSubCategoryIndex((prev) =>
        prev < subCategorySuggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedSubCategoryIndex((prev) => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === "Enter" && selectedSubCategoryIndex >= 0) {
      e.preventDefault();
      handleSubCategorySelect(subCategorySuggestions[selectedSubCategoryIndex]);
    } else if (e.key === "Escape") {
      setShowSubCategorySuggestions(false);
      setSelectedSubCategoryIndex(-1);
    }
  };

  // Helper function to get color for emotion
  const getEmotionColor = (emotion?: string): string => {
    const emotionColors: Record<string, string> = {
      Sad: "#3b82f6",
      Calm: "#10b981",
      Angry: "#ef4444",
      Joy: "#10b981",
      Complacent: "#f59e0b",
      Happy: "#10b981",
      Anxious: "#f59e0b",
      Stressed: "#f97316",
    };
    return emotionColors[emotion || ""] || "#6b7280";
  };

  // Helper function to get emotion definition
  const getEmotionDefinition = (emotion?: string): string => {
    const emotionDefinitions: Record<string, string> = {
      Enraged: "Feeling extremely angry and out of control. It's like your anger has reached its highest point, and you can't calm down.",
      Furious: "Very, very angry. It's a stronger feeling than just being mad or annoyed, almost like an explosive emotion.",
      Fuming: "Showing strong anger, almost like smoke coming out of your ears. You're so upset that it's hard to stay calm.",
      Disgusted: "Feeling very upset or sickened by something. It's like you can't stand what's happening or what you're experiencing.",
      Frightened: "Feeling scared or afraid. It's when something makes you feel really uneasy and worried about what might happen.",
      Lonely: "Feeling sad because you're alone or disconnected from others. It's a deep sense of isolation where you miss having someone around.",
      Depressed: "Feeling very sad for a long time. It's a heavy sadness that seems to stick with you and makes everything seem dull.",
      Panicked: "Feeling sudden and intense fear or worry. It's when you're overwhelmed and find it hard to think clearly or stay calm.",
      Nervous: "Feeling worried or uneasy about something. It's like having butterflies in your stomach because you're anxious about what's coming up.",
      Frustrated: "Feeling upset because things aren't going as planned. It's a mix of annoyance and disappointment when you hit obstacles or problems.",
      Angry: "Feeling strong displeasure or annoyance. It's when something bothers you a lot and makes you feel upset.",
      Worried: "Feeling anxious or concerned about something that might happen. It's when you're preoccupied with thoughts of potential problems or issues.",
      Miserable: "Feeling extremely unhappy or uncomfortable. It's a deep sense of unhappiness that makes everything seem worse.",
      Discouraged: "Feeling disheartened or lacking confidence. It's like losing hope or motivation because things aren't going as you hoped.",
      Shocked: "Feeling surprised or disturbed by something unexpected. It's a sudden, intense reaction to something that catches you off guard.",
      Restless: "Feeling unable to relax or stay still. It's like having too much energy or worry, making it hard to find calm or comfort.",
      Irritated: "Feeling annoyed or bothered by something. It's a persistent sense of discomfort or frustration, even if it's not overwhelming.",
      Disappointed: "Feeling sad because something didn't turn out as expected. It's when reality falls short of what you hoped for or anticipated.",
      Concerned: "Feeling worried about something. It's a mild form of anxiety or care about a situation that's important to you.",
      Tired: "Feeling like you need rest or sleep. It's when your energy is low, and you might feel drained but not necessarily unhappy.",
      Exhausted: "Feeling extremely tired. It's a state of being so low on energy that you need a lot of rest to recover.",
      Hyper: "Feeling overly energetic and excited. It's like having too much energy to stay still and being really animated.",
      Lively: "Feeling full of energy and enthusiasm. It's a sense of being excited and active, bringing a lot of spirit to whatever you're doing.",
      Energized: "Feeling full of energy and ready to go. It's when you feel awake, active, and motivated to take on tasks.",
      Satisfied: "Feeling pleased with how things are going. It's a sense of contentment and happiness with what you've achieved or have.",
      Complacent: "Feeling satisfied but maybe a bit too comfortable. It's when you're okay with the way things are and not seeking change or improvement.",
      Bored: "Feeling uninterested or restless because nothing is happening. It's a sense of dullness and lack of engagement with what's around you.",
      Uneasy: "Feeling slightly worried or uncomfortable. It's a mild sense of discomfort or nervousness that makes you feel unsettled.",
      Surprised: "Feeling astonished or amazed by something unexpected. It's a reaction to something that catches you off guard in a surprising way.",
      Enthusiastic: "Feeling excited and eager about something. It's a high level of interest and energy about a topic or activity.",
      Cheerful: "Feeling happy and full of joy. It's a bright and positive mood that makes you feel good and spread positivity.",
      Optimistic: "Feeling hopeful about the future. It's a positive outlook where you believe things will get better, even if there are challenges.",
      Focused: "Feeling concentrated and attentive to a task. It's being deeply engaged and determined to achieve something, without distractions.",
      Thoughtful: "Feeling considerate and reflective. It's when you carefully think about others' feelings or ideas and act with care.",
      Relaxed: "Feeling calm and at ease. It's a state where you're free from stress and worry, and you feel comfortable and serene.",
      Inspired: "Feeling motivated and excited by something or someone. It's when you're driven to act or create because you've been deeply moved.",
      Motivated: "Feeling driven to take action or achieve goals. It's a strong desire to pursue your goals and work towards what you want.",
      Proud: "Feeling pleased with something you or someone else has done. It's a sense of accomplishment and self-worth that comes from achievement.",
      Joyful: "Feeling great happiness and delight. It's an intense feeling of pleasure and contentment that brightens your day.",
      Peaceful: "Feeling calm and free from worry. It's a state of tranquility and quiet where you feel relaxed and at ease.",
      Hopeful: "Feeling optimistic about the future. It's a positive belief that things will improve and that better days are ahead.",
      Comfortable: "Feeling relaxed and at ease. It's when you're in a state of physical or emotional well-being and feel content.",
      Exhilarated: "Feeling extremely happy and excited. It's an intense and thrilling sense of joy and energy.",
      Thrilled: "Feeling very excited and happy. It's a high level of joy and enthusiasm about something exciting.",
      Blissful: "Feeling perfect happiness and contentment. It's an overwhelming sense of joy and peace that feels just right.",
      Grateful: "Feeling thankful for something. It's recognizing and appreciating the good things in your life and feeling appreciative.",
      Touched: "Feeling emotionally moved or affected by something. It's a deep sense of appreciation or warmth that impacts you deeply.",
      Carefree: "Feeling relaxed and without worries. It's a state of lightheartedness where you don't have any major concerns.",
      Serene: "Feeling calm and peaceful. It's a deep sense of tranquility and stillness where you feel completely at ease.",
    };
    return emotionDefinitions[emotion || ""] || "";
  };

  // Helper function to get color for zone
  const getZoneColor = (zone?: string): string => {
    const zoneColors: Record<string, string> = {
      Green: "#22c55e",
      Blue: "#3b82f6",
      Yellow: "#f59e0b",
      Orange: "#f97316",
      Brown: "#a16207",
      "Light Red": "#f87171",
      "Red Zone": "#ef4444",
      "Dark Red": "#dc2626",
    };
    return zoneColors[zone || ""] || "#6b7280";
  };

  // Calculate emotion and zone based on impact and pleasantness
  const calculateEmotionAndZone = (
    impact: number,
    pleasantness: number
  ): EmotionZoneResult => {
    // UI now uses 1–7 scale directly
    const impactScale = Math.min(7, Math.max(1, impact || 1));
    const pleasantnessScale = Math.min(7, Math.max(1, pleasantness || 1));

    const emotionZoneLookup: Record<string, EmotionZoneResult> = {
      // key: "impact-pleasantness"
      // P1=Dark Red, P2=Red Zone, P3=Brown Zone, P4=Yellow Zone, P5=Blue Zone, P6=Blue Zone, P7=Green Zone
      // (6 zones for 7 pleasantness levels — Blue Zone covers P5 and P6; no Orange in pleasantness mapping)
      "1-1": { calculatedEmotion: "Depressed",    calculatedZone: "Dark Red"    },
      "1-2": { calculatedEmotion: "Discouraged",  calculatedZone: "Red Zone"    },
      "1-3": { calculatedEmotion: "Exhausted",    calculatedZone: "Brown Zone"  },
      "1-4": { calculatedEmotion: "Uneasy",       calculatedZone: "Yellow Zone" },
      "1-5": { calculatedEmotion: "Relaxed",      calculatedZone: "Blue Zone"   },
      "1-6": { calculatedEmotion: "Comfortable",  calculatedZone: "Blue Zone"   },
      "1-7": { calculatedEmotion: "Serene",       calculatedZone: "Green Zone"  },
      "2-1": { calculatedEmotion: "Lonely",       calculatedZone: "Dark Red"    },
      "2-2": { calculatedEmotion: "Miserable",    calculatedZone: "Red Zone"    },
      "2-3": { calculatedEmotion: "Tired",        calculatedZone: "Brown Zone"  },
      "2-4": { calculatedEmotion: "Bored",        calculatedZone: "Yellow Zone" },
      "2-5": { calculatedEmotion: "Thoughtful",   calculatedZone: "Blue Zone"   },
      "2-6": { calculatedEmotion: "Hopeful",      calculatedZone: "Blue Zone"   },
      "2-7": { calculatedEmotion: "Carefree",     calculatedZone: "Green Zone"  },
      "3-1": { calculatedEmotion: "Frightened",   calculatedZone: "Dark Red"    },
      "3-2": { calculatedEmotion: "Worried",      calculatedZone: "Red Zone"    },
      "3-3": { calculatedEmotion: "Concerned",    calculatedZone: "Brown Zone"  },
      "3-4": { calculatedEmotion: "Complacent",   calculatedZone: "Yellow Zone" },
      "3-5": { calculatedEmotion: "Focused",      calculatedZone: "Blue Zone"   },
      "3-6": { calculatedEmotion: "Peaceful",     calculatedZone: "Blue Zone"   },
      "3-7": { calculatedEmotion: "Touched",      calculatedZone: "Green Zone"  },
      "4-1": { calculatedEmotion: "Disgusted",    calculatedZone: "Dark Red"    },
      "4-2": { calculatedEmotion: "Angry",        calculatedZone: "Red Zone"    },
      "4-3": { calculatedEmotion: "Disappointed", calculatedZone: "Brown Zone"  },
      "4-4": { calculatedEmotion: "Satisfied",    calculatedZone: "Yellow Zone" },
      "4-5": { calculatedEmotion: "Optimistic",   calculatedZone: "Blue Zone"   },
      "4-6": { calculatedEmotion: "Joyful",       calculatedZone: "Blue Zone"   },
      "4-7": { calculatedEmotion: "Grateful",     calculatedZone: "Green Zone"  },
      "5-1": { calculatedEmotion: "Fuming",       calculatedZone: "Dark Red"    },
      "5-2": { calculatedEmotion: "Frustrated",   calculatedZone: "Red Zone"    },
      "5-3": { calculatedEmotion: "Irritated",    calculatedZone: "Brown Zone"  },
      "5-4": { calculatedEmotion: "Energized",    calculatedZone: "Yellow Zone" },
      "5-5": { calculatedEmotion: "Cheerful",     calculatedZone: "Blue Zone"   },
      "5-6": { calculatedEmotion: "Proud",        calculatedZone: "Blue Zone"   },
      "5-7": { calculatedEmotion: "Blissful",     calculatedZone: "Green Zone"  },
      "6-1": { calculatedEmotion: "Furious",      calculatedZone: "Dark Red"    },
      "6-2": { calculatedEmotion: "Nervous",      calculatedZone: "Red Zone"    },
      "6-3": { calculatedEmotion: "Restless",     calculatedZone: "Brown Zone"  },
      "6-4": { calculatedEmotion: "Lively",       calculatedZone: "Yellow Zone" },
      "6-5": { calculatedEmotion: "Enthusiastic", calculatedZone: "Blue Zone"   },
      "6-6": { calculatedEmotion: "Motivated",    calculatedZone: "Blue Zone"   },
      "6-7": { calculatedEmotion: "Thrilled",     calculatedZone: "Green Zone"  },
      "7-1": { calculatedEmotion: "Enraged",      calculatedZone: "Dark Red"    },
      "7-2": { calculatedEmotion: "Panicked",     calculatedZone: "Red Zone"    },
      "7-3": { calculatedEmotion: "Shocked",      calculatedZone: "Brown Zone"  },
      "7-4": { calculatedEmotion: "Hyper",        calculatedZone: "Yellow Zone" },
      "7-5": { calculatedEmotion: "Surprised",    calculatedZone: "Blue Zone"   },
      "7-6": { calculatedEmotion: "Inspired",     calculatedZone: "Blue Zone"   },
      "7-7": { calculatedEmotion: "Exhilarated",  calculatedZone: "Green Zone"  },
    };

    const key = `${impactScale}-${pleasantnessScale}`;
    const result = emotionZoneLookup[key];

    if (result) {
      return result;
    }

    return {
      calculatedEmotion: "Unknown",
      calculatedZone: "Yellow Zone",
    };
  };

  // Update calculated emotion/zone when impact or joyfulness changes
  useEffect(() => {
    const { calculatedEmotion, calculatedZone } = calculateEmotionAndZone(
      formData.impact,
      formData.pleasantness
    );
    setFormData((prev) => ({
      ...prev,
      calculatedEmotion,
      calculatedZone,
    }));
  }, [formData.impact, formData.pleasantness]);

  const handleSubmit = () => {
    if (!formData.categoryId) {
      alert("Please select a Life Circle");
      return;
    }
    if (!formData.subCategoryId && !(isOtherSelected && otherText.trim())) {
      alert("Please select what is on top of your mind");
      return;
    }
    if (isOtherSelected && !otherText.trim()) {
      alert("Please enter what is on top of your mind");
      return;
    }
    if (!formData.impact || !formData.pleasantness) {
      alert("Please set both impact and pleasantness scales");
      return;
    }
    setShowConfirmModal(true);
  };

  const handleConfirm = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const moodLogData = {
        studentId: user?.id,
        date: formData.date,
        time: formData.time,
        feelingDescription: isOtherSelected ? otherText.trim() : (formData.subCategory || ""),
        categoryId: formData.categoryId === -1 ? null : (formData.categoryId || null),
        subCategoryId: formData.subCategoryId || null,
        addNote: formData.addNote || "",
        impact: formData.impact,
        joyfulness: formData.pleasantness,
        calculatedEmotion: formData.calculatedEmotion,
        calculatedZone: formData.calculatedZone,
        status: true,
      };

      console.log("Submitting mood log data:", moodLogData);

      const response = await axios.post(
        `${API_BASE_URL}/student-mood-logs`,
        moodLogData
      );

      if (response.status === 201) {
        navigate("/student/mood-history");
      }
    } catch (error: any) {
      setIsSubmitting(false);
      console.error("Error submitting mood log:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to log mood. Please try again.";
        
      if (errorMessage.includes("no longer exists in the database")) {
        alert(
          "Your session has expired. You will be logged out. Please log back in."
        );
        logout();
        navigate("/student/login");
        return;
      }
        
      alert(
        `Error: ${errorMessage}\n\nPlease make sure the backend server is restarted.`
      );
    }
  };

  return (
    <div
      style={{
        padding: "24px",
        backgroundColor: "#fafbfc",
        minHeight: "100vh",
        fontFamily:
          '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        maxWidth: "1600px",
        margin: "0 auto",
      }}
    >
      {/* Custom styles for slider gradient and modal animations */}
      <style>{`
        .slider-gradient {
          width: 100%;
          height: 12px;
          border-radius: 6px;
          outline: none;
          -webkit-appearance: none;
          appearance: none;
          background: linear-gradient(
            to right,
            #8B0000 0%,
            #FF4500 20%,
            #FFA500 35%,
            #90EE90 50%,
            #87CEEB 65%,
            #4169E1 85%,
            #00008B 100%
          );
        }
        .slider-gradient::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: #fff;
          cursor: pointer;
          border: 2px solid #3498db;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
          transition: all 0.2s;
        }
        .slider-gradient::-webkit-slider-thumb:hover {
          transform: scale(1.15);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        }
        .slider-gradient::-moz-range-thumb {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: #fff;
          cursor: pointer;
          border: 2px solid #3498db;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
          transition: all 0.2s;
        }
        .slider-gradient::-moz-range-thumb:hover {
          transform: scale(1.15);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        }
        @keyframes fadeInOverlay {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideInModal {
          from {
            opacity: 0;
            transform: scale(0.9) translateY(-20px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
        .modal-overlay-custom {
          animation: fadeInOverlay 0.3s ease-out;
        }
        .modal-custom {
          animation: slideInModal 0.4s ease-out;
        }
      `}</style>
      {/* Main Form Container */}
      <div
        style={{
          background: "#fff",
          borderRadius: "14px",
          padding: "32px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
          maxWidth: "800px",
          margin: "0 auto",
        }}
      >
        {/* Title */}
        <h2 className="text-[28px] font-bold m-0 mb-8 text-[#222]">
          What are you feeling right now?
        </h2>

        {/* Date and Time Display */}
        <div className="flex gap-4 mb-6 text-sm text-[#666]">
          <div>
            <strong>Date:</strong> {formData.date}
          </div>
          <div>
            <strong>Time:</strong> {formData.time}
          </div>
        </div>

        {/* What is on top of your mind? Input (uses subcategory data) */}
        <div style={{ marginBottom: "32px", position: "relative" }}>
          <label
            style={{
              display: "block",
              fontSize: "14px",
              fontWeight: "600",
              color: "#444",
              marginBottom: "8px",
            }}
          >
            What is on top of your mind?
          </label>
          {!isOtherSelected ? (
            <>
              <input
                ref={subCategoryInputRef}
                type="text"
                placeholder="Type what's on top of your mind..."
                value={formData.subCategory}
                onChange={handleSubCategoryChange}
                onKeyDown={handleSubCategoryKeyDown}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  border: "1px solid #e0e0e0",
                  borderRadius: "10px",
                  fontSize: "16px",
                  fontFamily: "inherit",
                  background: "#f9f9f9",
                  transition: "all 0.2s",
                  boxSizing: "border-box",
                }}
                onFocus={(e) => {
                  e.target.style.outline = "none";
                  e.target.style.borderColor = "#00c7b7";
                  e.target.style.background = "#fff";
                  e.target.style.boxShadow = "0 0 0 3px rgba(0, 199, 183, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#e0e0e0";
                  e.target.style.background = "#f9f9f9";
                  e.target.style.boxShadow = "none";
                }}
              />
              {/* Subcategory Autocomplete Suggestions Dropdown */}
              {showSubCategorySuggestions && (
                <div
                  ref={subCategorySuggestionsRef}
                  className="absolute top-full left-0 right-0 bg-white border border-[#e0e0e0] rounded-[10px] shadow-[0_4px_12px_rgba(0,0,0,0.15)] z-[1000] mt-1 max-h-[300px] overflow-y-auto"
                >
                  {subCategorySuggestions.map((subCat, index) => (
                    <div
                      key={subCat.id || index}
                      onClick={() => handleSubCategorySelect(subCat)}
                      className={`px-4 py-3 cursor-pointer border-b border-[#f0f0f0] transition-colors duration-200 ${
                        index === selectedSubCategoryIndex
                          ? "bg-[#f5f5f5]"
                          : "bg-white"
                      }`}
                      onMouseEnter={() => setSelectedSubCategoryIndex(index)}
                      onMouseLeave={() => setSelectedSubCategoryIndex(-1)}
                    >
                      <div className="text-[15px] font-semibold text-[#222] leading-snug">
                        {subCat.name}
                      </div>
                    </div>
                  ))}
                  {/* Others option */}
                  <div
                    onClick={handleOtherSelect}
                    className="px-4 py-3 cursor-pointer transition-colors duration-200 hover:bg-[#f5f5f5] border-t border-[#e0e0e0]"
                  >
                    <div className="text-[15px] font-semibold text-[#00c7b7] leading-snug">
                      Others (Enter manually)
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <input
                type="text"
                placeholder="Enter your own text..."
                value={otherText}
                onChange={(e) => setOtherText(e.target.value)}
                autoFocus
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  border: "1px solid #00c7b7",
                  borderRadius: "10px",
                  fontSize: "16px",
                  fontFamily: "inherit",
                  background: "#fff",
                  transition: "all 0.2s",
                  boxSizing: "border-box",
                  boxShadow: "0 0 0 3px rgba(0, 199, 183, 0.1)",
                }}
                onFocus={(e) => {
                  e.target.style.outline = "none";
                  e.target.style.borderColor = "#00c7b7";
                  e.target.style.boxShadow = "0 0 0 3px rgba(0, 199, 183, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#00c7b7";
                  e.target.style.boxShadow = "none";
                }}
              />
              <button
                type="button"
                onClick={() => {
                  setIsOtherSelected(false);
                  setOtherText("");
                  setFormData((prev) => ({
                    ...prev,
                    subCategory: "",
                    subCategoryId: null,
                    category: "",
                    categoryId: null,
                  }));
                  setShowCategorySelection(false);
                }}
                style={{
                  padding: "10px 16px",
                  borderRadius: "10px",
                  fontSize: "13px",
                  fontWeight: "600",
                  cursor: "pointer",
                  border: "1px solid #e0e0e0",
                  background: "#f0f0f0",
                  color: "#666",
                  whiteSpace: "nowrap",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#e0e0e0";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#f0f0f0";
                }}
              >
                Back
              </button>
            </div>
          )}
        </div>

        {/* Life Circles (Category Selection) - shown when category needs to be selected or when auto-selected */}
        {(showCategorySelection || formData.categoryId) && (
          <div style={{ marginBottom: "32px" }}>
            <label
              style={{
                display: "block",
                fontSize: "14px",
                fontWeight: "600",
                color: "#444",
                marginBottom: "8px",
              }}
            >
              Life Circles
            </label>
            {showCategorySelection && !isOtherSelected && (
              <p
                style={{
                  fontSize: "12px",
                  color: "#777",
                  marginBottom: "12px",
                  fontStyle: "italic",
                }}
              >
                Please select the appropriate Life Circle for your selection.
              </p>
            )}
            {isOtherSelected && (
              <p
                style={{
                  fontSize: "12px",
                  color: "#777",
                  marginBottom: "12px",
                  fontStyle: "italic",
                }}
              >
                "Others" is selected by default. You can change the Life Circle if needed.
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              {categories
                .filter((cat) =>
                  showCategorySelection && !isOtherSelected
                    ? selectedCategories.includes(cat.id)
                    : true
                )
                .map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => handleCategorySelect(cat)}
                    className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                      formData.categoryId === cat.id
                        ? "bg-[#00c7b7] text-white border-[#00c7b7]"
                        : "bg-white text-[#444] border-[#e0e0e0] hover:border-[#00c7b7]"
                    }`}
                    style={{
                      cursor: "pointer",
                    }}
                  >
                    {cat.name}
                  </button>
                ))}
              {/* Others category option */}
              <button
                type="button"
                onClick={() => {
                  setFormData((prev) => ({
                    ...prev,
                    category: "Others",
                    categoryId: -1,
                  }));
                  setShowCategorySelection(false);
                }}
                className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                  formData.categoryId === -1
                    ? "bg-[#00c7b7] text-white border-[#00c7b7]"
                    : "bg-white text-[#444] border-[#e0e0e0] hover:border-[#00c7b7]"
                }`}
                style={{
                  cursor: "pointer",
                }}
              >
                Others
              </button>
            </div>
          </div>
        )}

        {/* Sliders - only shown after category and subcategory are filled */}
        {formData.categoryId && (formData.subCategoryId || (isOtherSelected && otherText.trim())) && (
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-[#444] mb-6">
                Please elaborate the impact and feeling for your selection from the scales below
              </h3>

              {/* Q3: Impact Slider */}
              <div style={{ marginBottom: "32px" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "16px",
                  }}
                >
                  <label
                    style={{
                      fontSize: "16px",
                      fontWeight: "600",
                      color: "#444",
                    }}
                  >
                    On a scale of 1 to 7, how strongly is this affecting you? (from low → high & long lasting)
                  </label>
                  <span
                    style={{
                      fontSize: "16px",
                      fontWeight: "600",
                      color: "#444",
                    }}
                  >
                    {formData.impact}/7
                  </span>
                </div>
                <div
                  style={{
                    background: "#f9f9f9",
                    padding: "16px",
                    borderRadius: "10px",
                    marginBottom: "12px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "13px",
                      color: "#666",
                      marginBottom: "12px",
                      lineHeight: "1.5",
                    }}
                  >
                    <strong>Scale:</strong> Rate how strongly this is affecting you on a scale from 1 to 7, with 1 being low and 7 being high & long lasting.
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="7"
                    value={formData.impact}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        impact: parseInt(e.target.value, 10),
                      })
                    }
                    className="slider-gradient"
                  />
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: "12px",
                      color: "#666",
                      marginTop: "8px",
                    }}
                  >
                    <span>1 - Low</span>
                    <span>7 - High & Long Lasting</span>
                  </div>
                </div>
              </div>

              {/* Pleasantness Slider */}
              <div style={{ marginBottom: "32px" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "16px",
                  }}
                >
                  <label
                    style={{
                      fontSize: "16px",
                      fontWeight: "600",
                      color: "#444",
                    }}
                  >
                    On a scale of 1 to 7, how does this feel for you? (from very uncomfortable → comfortable)
                  </label>
                  <span
                    style={{
                      fontSize: "16px",
                      fontWeight: "600",
                      color: "#444",
                    }}
                  >
                    {formData.pleasantness}/7
                  </span>
                </div>
                <div
                  style={{
                    background: "#f9f9f9",
                    padding: "16px",
                    borderRadius: "10px",
                    marginBottom: "12px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "13px",
                      color: "#666",
                      marginBottom: "12px",
                      lineHeight: "1.5",
                    }}
                  >
                    <strong>Scale:</strong> Rate how this feels for you on a scale from 1 to 7, with 1 being very uncomfortable and 7 being comfortable.
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="7"
                    value={formData.pleasantness}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        pleasantness: parseInt(e.target.value, 10),
                      })
                    }
                    className="slider-gradient"
                  />
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: "12px",
                      color: "#666",
                      marginTop: "8px",
                    }}
                  >
                    <span>1 - Very Uncomfortable</span>
                    <span>7 - Comfortable</span>
                  </div>
                </div>
              </div>

              {/* Calculated Emotion and Zone Display */}
              {(formData.calculatedEmotion || formData.calculatedZone) && (
                <div className="bg-[#e8f5e9] p-4 rounded-[10px] mt-4">
                  <div className="text-sm text-[#666] mb-3">
                    Calculated based on your Impact and Pleasantness values:
                  </div>
                  <div className="space-y-3">
                    {formData.calculatedEmotion && (
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <div
                            className="w-6 h-6 rounded-full border-2 border-white shadow-[0_2px_4px_rgba(0,0,0,0.1)]"
                            style={{
                              backgroundColor: getEmotionColor(
                                formData.calculatedEmotion
                              ),
                            }}
                          />
                          <span className="text-base font-semibold text-[#222]">
                            {formData.calculatedEmotion}
                          </span>
                        </div>
                        {getEmotionDefinition(formData.calculatedEmotion) && (
                          <div className="text-sm text-[#555] ml-8 italic">
                            {getEmotionDefinition(formData.calculatedEmotion)}
                          </div>
                        )}
                      </div>
                    )}
                    {formData.calculatedZone && (
                      <div className="flex items-center gap-2">
                        <div
                          className="w-6 h-6 rounded-full border-2 border-white shadow-[0_2px_4px_rgba(0,0,0,0.1)]"
                          style={{
                            backgroundColor: getZoneColor(
                              formData.calculatedZone
                            ),
                          }}
                        />
                        <span className="text-sm font-semibold text-[#222]">
                          {formData.calculatedZone}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

        {/* Submit Button */}
        <div
          style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}
        >
          <button
            onClick={() => navigate("/student/dashboard")}
            style={{
              padding: "12px 28px",
              borderRadius: "10px",
              fontSize: "15px",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.2s",
              border: "none",
              fontFamily: "inherit",
              background: "#f0f0f0",
              color: "#666",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#e0e0e0";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#f0f0f0";
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={
              !formData.categoryId ||
              (!formData.subCategoryId && !(isOtherSelected && otherText.trim())) ||
              !formData.impact ||
              !formData.pleasantness
            }
            style={{
              padding: "12px 28px",
              borderRadius: "10px",
              fontSize: "15px",
              fontWeight: "600",
              cursor:
                !formData.categoryId ||
                (!formData.subCategoryId && !(isOtherSelected && otherText.trim())) ||
                !formData.impact ||
                !formData.pleasantness
                  ? "not-allowed"
                  : "pointer",
              transition: "all 0.2s",
              border: "none",
              fontFamily: "inherit",
              background:
                !formData.categoryId ||
                (!formData.subCategoryId && !(isOtherSelected && otherText.trim())) ||
                !formData.impact ||
                !formData.pleasantness
                  ? "#cccccc"
                  : "#00c7b7",
              color: "#fff",
              opacity:
                !formData.categoryId ||
                (!formData.subCategoryId && !(isOtherSelected && otherText.trim())) ||
                !formData.impact ||
                !formData.pleasantness
                  ? 0.6
                  : 1,
            }}
            onMouseEnter={(e) => {
              if (
                formData.categoryId &&
                (formData.subCategoryId || (isOtherSelected && otherText.trim())) &&
                formData.impact &&
                formData.pleasantness
              ) {
                e.currentTarget.style.background = "#009e8e";
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.boxShadow =
                  "0 4px 12px rgba(0, 199, 183, 0.3)";
              }
            }}
            onMouseLeave={(e) => {
              if (
                formData.categoryId &&
                (formData.subCategoryId || (isOtherSelected && otherText.trim())) &&
                formData.impact &&
                formData.pleasantness
              ) {
                e.currentTarget.style.background = "#00c7b7";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }
            }}
          >
            Submit Mood Log
          </button>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div
          className="modal-overlay-custom"
          onClick={() => setShowConfirmModal(false)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "20px",
            backdropFilter: "blur(8px)",
          }}
        >
          <div
            className="modal-custom"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: "16px",
              padding: "32px",
              maxWidth: "600px",
              width: "100%",
              maxHeight: "90vh",
              overflowY: "auto",
              boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "24px",
              }}
            >
              <h3
                style={{
                  fontSize: "22px",
                  fontWeight: "700",
                  color: "#222",
                  margin: 0,
                }}
              >
                Are you want to submit? Please confirm to proceed
              </h3>
              <button
                onClick={() => setShowConfirmModal(false)}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "28px",
                  color: "#888",
                  cursor: "pointer",
                  padding: 0,
                  width: "32px",
                  height: "32px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "8px",
                  transition: "background 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#f0f0f0";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                }}
              >
                ×
              </button>
            </div>
            <div style={{ marginBottom: "24px" }}>
              <div
                style={{
                  background: "#f9f9f9",
                  padding: "20px",
                  borderRadius: "12px",
                  marginBottom: "20px",
                }}
              >
                <div style={{ display: "grid", gap: "16px" }}>
                  {(formData.subCategory || (isOtherSelected && otherText.trim())) && (
                    <div>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#888",
                          marginBottom: "4px",
                        }}
                      >
                        What is on top of your mind?
                      </div>
                      <div style={{ fontWeight: "600" }}>
                        {isOtherSelected ? otherText.trim() : formData.subCategory}
                      </div>
                    </div>
                  )}
                  {formData.category && (
                    <div>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#888",
                          marginBottom: "4px",
                        }}
                      >
                        Life Circles
                      </div>
                      <div style={{ fontWeight: "600" }}>
                        {formData.category}
                      </div>
                    </div>
                  )}
                  <div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#888",
                        marginBottom: "4px",
                      }}
                    >
                      Impact & Pleasantness
                    </div>
                    <div style={{ fontWeight: "600" }}>
                      Impact: {formData.impact}/7 | Pleasantness:{" "}
                      {formData.pleasantness}/7
                    </div>
                  </div>
                  {(formData.calculatedEmotion || formData.calculatedZone) && (
                    <div>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#888",
                          marginBottom: "8px",
                        }}
                      >
                        Calculated Emotion & Zone
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "16px",
                          flexWrap: "wrap",
                        }}
                      >
                        {formData.calculatedEmotion && (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <div
                              style={{
                                width: "24px",
                                height: "24px",
                                borderRadius: "50%",
                                backgroundColor: getEmotionColor(
                                  formData.calculatedEmotion
                                ),
                                border: "2px solid #fff",
                                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                              }}
                            />
                            <span
                              style={{ fontWeight: "600", fontSize: "14px" }}
                            >
                              {formData.calculatedEmotion}
                            </span>
                          </div>
                        )}
                        {formData.calculatedZone && (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <div
                              style={{
                                width: "24px",
                                height: "24px",
                                borderRadius: "50%",
                                backgroundColor: getZoneColor(
                                  formData.calculatedZone
                                ),
                                border: "2px solid #fff",
                                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                              }}
                            />
                            <span
                              style={{ fontWeight: "600", fontSize: "14px" }}
                            >
                              {formData.calculatedZone}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <p style={{ color: "#666", fontSize: "14px" }}>
                Please review your mood log details before submitting.
              </p>
            </div>
            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={() => setShowConfirmModal(false)}
                style={{
                  padding: "12px 28px",
                  borderRadius: "10px",
                  fontSize: "15px",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  border: "none",
                  fontFamily: "inherit",
                  background: "#f0f0f0",
                  color: "#666",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#e0e0e0";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#f0f0f0";
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={isSubmitting}
                style={{
                  padding: "12px 28px",
                  borderRadius: "10px",
                  fontSize: "15px",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  border: "none",
                  fontFamily: "inherit",
                  background: "#00c7b7",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#009e8e";
                  e.currentTarget.style.transform = "translateY(-1px)";
                  e.currentTarget.style.boxShadow =
                    "0 4px 12px rgba(0, 199, 183, 0.3)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#00c7b7";
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <FiCheck /> {isSubmitting ? "Submitting..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LogMood;
